import React, { useCallback, useEffect, useState } from "react";
import { ReactComponent as Info } from "../../../../../icons/info-Simulator.svg";
import loader from "../../../../../components/recharts/loader.gif";
import {
  fetchOptimizerCurrentOptimisation,
  OptimizerCurrentUpdateData,
} from "../../../../../Rest_api/DemoRestApi";
import { OptimizierCurrentUpdate } from "../../../../../store/state/GNAOptimizer/CurrentOptimisationState";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeft } from "lucide-react";
import Loading from "../../../../../components/Loading";

interface ScenarioState {
  currentCapacity: number;
  simulateCapacity: number;
  rate: number;
  surcharge: number;
  reserve: number;
  threshold: number;
  // tolerance: number;
}

interface SimulationScenarioProps {
  onApply: (data: ScenarioState) => void;
  onReset: () => void;
  loading: boolean;
  showSliders: boolean; // New prop to control display mode
}

const sliderConfig = {
  currentCapacity: { min: 0, max: 10000, step: 10, unit: "MW" },
  simulateCapacity: { min: 0, max: 10000, step: 10, unit: "MW" },
  rate: { min: 0, max: 10, step: 0.1, unit: "₹/kWh" },
  surcharge: { min: 0, max: 100, step: 0.1, unit: "%" },
  reserve: { min: 0, max: 10000, step: 10, unit: "MW" },
  threshold: { min: 0, max: 250, step: 10, unit: "MW" },
  // tolerance: { min: 0, max: 250, step: 1, unit: "MW" },
};

export function SimulationScenario({
  onApply,
  onReset,
  loading,
  showSliders,
}: SimulationScenarioProps) {
  const optimizerCurrent = useSelector(
    (state: any) => state.optimizierData.data.optimizerCurrent
  );

  const getInitialState = useCallback((): ScenarioState => {
    if (!optimizerCurrent) {
      return {
        currentCapacity: 0,
        simulateCapacity: 0,
        rate: 0,
        surcharge: 0,
        reserve: 0,
        threshold: 0,
        // tolerance: 0,
      };
    }
    return {
      currentCapacity: optimizerCurrent.currentCapacity ?? 0,
      simulateCapacity: optimizerCurrent.simulateCapacity ?? 0,
      rate: optimizerCurrent.rate ?? 0,
      surcharge: optimizerCurrent.surcharge ?? 0,
      reserve: optimizerCurrent.reserve ?? 0,
      threshold: optimizerCurrent.threshold ?? 0,
      // tolerance: optimizerCurrent.tolerance ?? 0,
    };
  }, [optimizerCurrent]);

  const [formData, setFormData] = useState<ScenarioState>(getInitialState);

  useEffect(() => {
    if (optimizerCurrent) {
      setFormData(getInitialState());
    }
  }, [getInitialState, optimizerCurrent]);

  const handleSliderChange = (field: keyof ScenarioState, value: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInputChange = (field: keyof ScenarioState, value: string) => {
    const numValue = parseFloat(value) || 0;
    const config = sliderConfig[field];
    // Clamp value between min and max
    const clampedValue = Math.min(Math.max(numValue, config.min), config.max);
    setFormData((prev) => ({
      ...prev,
      [field]: clampedValue,
    }));
  };

  const handleReset = () => {
    setFormData(getInitialState);
    onReset();
  };

  const handleApply = () => {
    onApply(formData);
  };

  const calculatePercentage = (field: keyof ScenarioState) => {
    const config = sliderConfig[field];
    const value = formData[field];
    return ((value - config.min) / (config.max - config.min)) * 100;
  };

  const formatValue = (field: keyof ScenarioState) => {
    const value = formData[field];
    if (field === "rate") {
      return value.toFixed(2);
    }
    return value.toString();
  };

  // Render input field component
  const renderInputField = (
    field: keyof ScenarioState,
    label: string,
    showInfo: boolean = false
  ) => (
    <div className="border-b border-[#F3F3F4] pb-2 mb-2">
      <div className="flex flex-wrap items-center justify-start mb-2 gap-2">
        <label className="text-xs text-[#8C8A96] font-medium">{label}</label>
        {/* {showInfo && (
          <div className="relative group inline-block">
            <Info className="w-5 h-5 text-[#C5C5CBD9] cursor-pointer" />
            <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white text-[#F1935C] text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 border border-[#F1935C] shadow-lg z-10">
              This is a tooltip
            </span>
          </div>
        )} */}
      </div>
      <input
        type="number"
        min={sliderConfig[field].min}
        max={sliderConfig[field].max}
        step={sliderConfig[field].step}
        value={formData[field]}
        onChange={(e) => handleInputChange(field, e.target.value)}
        className="w-full px-2 py-2 text-[#6F6D7C] text-sm border border-gray-300 font-medium rounded-lg focus:outline-none focus:ring-1 focus:ring-[#F1935C] focus:border-transparent"
        placeholder={`Enter ${label}`}
      />
      {/* <span className="text-xs text-gray-400 mt-1">
        {sliderConfig[field].unit}
      </span> */}
    </div>
  );

  // Render slider component
  const renderSlider = (
    field: keyof ScenarioState,
    label: string,
    showInfo: boolean = false
  ) => (
    <div className="border-b border-[#F3F3F4] mb-2">
      <div className="pb-1">
        <div className="flex items-center justify-start mb-6 gap-2">
          <label className="text-xs text-[#8C8A96] font-medium">{label}</label>
          {showInfo && (
            <div className="relative inline-block">
              <Info className="w-5 h-5 text-[#C5C5CBD9] cursor-pointer group" />

              <span
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 
    whitespace-nowrap bg-white text-[#F1935C] text-xs px-2 py-1 rounded 
    opacity-0 group-hover:opacity-100 transition-opacity duration-200 
    border border-[#F1935C] shadow-lg z-10"
              >
                This is a tooltip
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="text-xs text-gray-400 mt-1">
            {sliderConfig[field].min}
          </span>
          <div className="relative w-full">
            <div
              className="absolute -top-5 text-xs font-semibold text-[#F1935C]"
              style={{
                left: `calc(${calculatePercentage(field)}% - 16px)`,
              }}
            >
              {formatValue(field)}
              {field === "surcharge" && "%"}
            </div>
            <input
              type="range"
              min={sliderConfig[field].min}
              max={sliderConfig[field].max}
              step={sliderConfig[field].step}
              value={formData[field]}
              onChange={(e) =>
                handleSliderChange(field, parseFloat(e.target.value))
              }
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #F1935C ${calculatePercentage(
                  field
                )}%, #e5e7eb ${calculatePercentage(field)}%)`,
              }}
            />
          </div>
          <span className="text-xs text-gray-400 mt-1">
            {sliderConfig[field].max}
            {field === "surcharge" && "%"}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="px-2  w-full h-full">
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #F1935C;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #F1935C;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>

      {/* Present GNA/TGNA Section */}
      <h3 className="text-base text-[#34656d] font-medium mb-3">
        Present GNA-TGNA
      </h3>

      {showSliders ? (
        <>
          {renderSlider("currentCapacity", "GNA Capacity (MW)")}
          {renderSlider("simulateCapacity", "Simulate GNA Capacity (MW)")}
          {renderSlider("rate", "GNA Rate (₹/kWh)")}
          {renderSlider("surcharge", "TGNA Surcharge (%)")}
        </>
      ) : (
        <>
          {renderInputField("currentCapacity", "GNA Capacity (MW)")}
          {renderInputField("simulateCapacity", "Simulate GNA Capacity (MW)")}
          {renderInputField("rate", "GNA Rate (₹/kWh)")}
          {renderInputField("surcharge", "TGNA Surcharge (%)")}
        </>
      )}

      {/* GNA/TGNA Constraints Section */}
      <h3 className="text-base text-[#34656d] font-medium my-3">
        GNA-TGNA Constraints
      </h3>

      {showSliders ? (
        <>
          {renderSlider("reserve", "Base Line GNA Capacity (MW)", true)}
          {renderSlider("threshold", "Min TGNA Threshold (MW)", true)}
          {/* {renderSlider("tolerance", "Min TGNA Tolerance Instances", true)} */}
        </>
      ) : (
        <>
          {renderInputField("reserve", "Base Line GNA Capacity (MW)", true)}
          {renderInputField("threshold", "Min TGNA Threshold (MW)", true)}
          {/* {renderInputField("tolerance", "Min TGNA Tolerance Instances", true)} */}
        </>
      )}

      {/* Action Buttons */}
      {showSliders ? (
        <>
          <div className="flex justify-start gap-2 py-2">
            <button
              onClick={handleReset}
              className="w-[171px] h-[42px] text-base font-medium text-[#6F6D7C] bg-transparent border-none rounded-lg cursor-pointer hover:text-gray-700 transition-colors"
            >
              Reset
            </button>
            <button
              onClick={handleApply}
              className="w-[171px] h-[42px] text-base font-medium text-white bg-[#F1935C] rounded-lg cursor-pointer shadow-md shadow-orange-500/20 hover:bg-[#cd733f] transition-colors"
            >
              {loading ? "Loading..." : "Apply"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-start gap-2 py-2">
            <button
              onClick={handleReset}
              className="w-[171px] h-[42px] text-base font-medium text-[#6F6D7C] bg-transparent border-none rounded-lg cursor-pointer hover:text-gray-700 transition-colors"
            >
              Reset
            </button>
            <button
              onClick={handleApply}
              className="w-[171px] h-[42px] text-base font-medium text-white bg-[#F1935C] rounded-lg cursor-pointer shadow-md shadow-orange-500/20 hover:bg-[#cd733f] transition-colors"
            >
              {loading ? "Loading..." : "Apply"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Main SimulationTable component
export default function SimulationOptimisation({
  open,
  setOpen,
  setError,
  refreshKey,
  setRefreshKey,
}: {
  open: boolean;
  setOpen: any;
  setError?: any;
  refreshKey: any;
  setRefreshKey: any;
}) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [fullbtn, setFullBtn] = useState(false);

  const handleApply = async (scenarioData: ScenarioState) => {
    setLoading(true);
    try {
      const payload = scenarioData;
      await OptimizerCurrentUpdateData(payload);
      setRefreshKey((prev: number) => prev + 1);
    } catch (error) {
      console.error("Error submitting data", error);
      setError("Error submitting data");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const CurrentData = await fetchOptimizerCurrentOptimisation();
      dispatch(OptimizierCurrentUpdate({ current: CurrentData }));
    } catch (err) {
      console.error("Error fetching optimizer current data", err);
      if (setError) setError("Error fetching data");
    }
  }, [dispatch, setError]);

  useEffect(() => {
    fetchData();
  }, [refreshKey, fetchData]);

  const handleReset = () => {};

  return (
    <div
      className="gas-chart-container simulation_table shadow-[rgba(149,157,165,0.2)_0px_8px_24px] bg-white overflow-y-auto overflow-hidden"
      id="slide"
      style={{
        width: open ? "18%" : "27%",
        position: "relative",
        zIndex: "2",
        height: "calc(-195px + 100vh)",
        transition: "all 0.3s ease-out",
        // paddingRight: open ? "1rem" : "12px",
      }}
    >
      <button
        style={{
          width: fullbtn ? "100px" : "30px",
          overflow: "hidden",
          height: "30px",
          right: "0px",
        }}
        onMouseOver={() => setFullBtn(true)}
        onMouseLeave={() => setFullBtn(false)}
        onClick={() => setOpen(!open)}
        className="hide-btn flex items-center justify-between gap-1 transition-all text-nowrap duration-700 ease-in-out"
      >
        <ChevronLeft
          size={18}
          className={`text-primary transition-transform duration-300 ${
            !open ? "rotate-359" : "rotate-180"
          }`}
        />
        {fullbtn && (
          <>{open ? <span>Show Sliders</span> : <span>Hide Sliders</span>}</>
        )}
      </button>
      {loading && <Loading/>}
      <SimulationScenario
        loading={loading}
        onApply={handleApply}
        onReset={handleReset}
        showSliders={!open}
      />
    </div>
  );
}
